import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { SkillsMatrix, Props as SkillsMatrixProps } from './SkillsMatrix';
import { SkillSingleSelect } from '../common/SkillSingleSelect';
import Skeleton from 'react-loading-skeleton';
import ServerError from '../../../../../components/ServerError';
import styled from '@emotion/styled';
import { useSkillsMatrix } from './useSkillsMatrix';
import { SelectSingleEmployeeConnected } from '../../../components/Selects/SelectSingleEmployeeConnected';
import Button from '../../../../../components/button';
import ToggleGroup from '../../../../../components/button/ToggleGroup';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { useFlairLocalStorage } from '../../../hooks/useFlairLocalStorage';
import { SkillCategoryFilter } from '../manager/SkillCategoryFilter';
import { filterByCategoryIds } from '../common/mappings';
import { CategoryFilterEmptyState } from '../common/CategoryFilterEmptyState';
import { isSkillGroupBy } from '../common/skillGrouping';
import { resolveSkillName as resolveLocalizedSkillName } from '../common/resolveSkillName';

type Props = Pick<SkillsMatrixProps, 'onEmployeeClick'>;

export const SkillsMatrixConnected: React.FC<React.PropsWithChildren<Props>> = (
  props,
) => {
  const t = useNamespacedTranslation('skills.matrix');
  const tGrouping = useNamespacedTranslation('skills.grouping');
  const { i18n } = useTranslation();
  const [categoryFilter, setCategoryFilter] = useFlairLocalStorage(
    'skillsMatrix_categoryIds',
    [],
  );
  const [grouping, setGrouping] = useFlairLocalStorage(
    'skillsMatrix_grouping',
    'skill',
  );
  const [employeeFilter, setEmployeeFilter] = useFlairLocalStorage(
    'skillsMatrix_employeeFilter',
    null,
  );
  const {
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedSkillIds,
    setSelectedSkillIds,
    allSkills,
    notSelectedSkills,
    selectedEmployeeInfos,
    selectedSkills,
    employeeSkills,
    error,
    resetSelection,
  } = useSkillsMatrix();

  const filteredSelectedSkills = useMemo(
    () => selectedSkills && filterByCategoryIds(selectedSkills, categoryFilter),
    [selectedSkills, categoryFilter],
  );
  const filteredEmployees = useMemo(() => {
    if (!selectedEmployeeInfos) {
      return selectedEmployeeInfos;
    }
    const query = employeeFilter?.trim().toLowerCase();
    if (!query) {
      return selectedEmployeeInfos;
    }
    return selectedEmployeeInfos.filter((e) =>
      e.name.toLowerCase().includes(query),
    );
  }, [selectedEmployeeInfos, employeeFilter]);

  // Show a filter-specific empty message when the name filter hides everyone,
  // instead of the "select someone" copy meant for an empty selection.
  const emptyEmployeesText =
    employeeFilter?.trim() &&
    (selectedEmployeeInfos?.length ?? 0) > 0 &&
    (filteredEmployees?.length ?? 0) === 0
      ? t('noEmployeesMatchFilter')
      : undefined;

  const resolveSkillName = useCallback(
    (skillId: string) => {
      const skill = allSkills?.find((s) => s.id === skillId);
      return skill
        ? resolveLocalizedSkillName(
            skill.baseName,
            skill.translations,
            i18n.language,
          ).text
        : undefined;
    },
    [allSkills, i18n.language],
  );

  const handleAddEmployee = useCallback(
    (employeeId: string | null) => {
      if (selectedEmployeeIds === null) {
        return;
      }
      if (employeeId) {
        setSelectedEmployeeIds(_.uniq([...selectedEmployeeIds, employeeId]));
      }
    },
    [selectedEmployeeIds, setSelectedEmployeeIds],
  );

  const handleResetClick = useCallback(() => {
    resetSelection();
    setCategoryFilter([]);
    setGrouping('skill');
    setEmployeeFilter(null);
  }, [resetSelection, setCategoryFilter, setGrouping, setEmployeeFilter]);

  const handleRemoveEmployee = useCallback(
    (employeeId: string) => {
      if (selectedEmployeeIds === null) {
        return;
      }
      setSelectedEmployeeIds(
        selectedEmployeeIds.filter((x) => x !== employeeId),
      );
    },
    [setSelectedEmployeeIds, selectedEmployeeIds],
  );

  const handleAddSkill = useCallback(
    (skillId: string | null) => {
      if (selectedSkillIds === null) {
        return;
      }
      if (skillId) {
        setSelectedSkillIds(_.uniq([...selectedSkillIds, skillId]));
      }
    },
    [setSelectedSkillIds, selectedSkillIds],
  );

  const handleRemoveSkill = useCallback(
    (skillId: string) => {
      if (selectedSkillIds === null) {
        return;
      }
      setSelectedSkillIds(selectedSkillIds.filter((x) => x !== skillId));
    },
    [setSelectedSkillIds, selectedSkillIds],
  );

  if (error) {
    return <ServerError />;
  }

  const renderSkillSelect = () => {
    // Offer every not-yet-added skill regardless of the category filter (which
    // only narrows the displayed columns), so skills stay addable while filtering.
    return notSelectedSkills ? (
      <SkillSingleSelect
        value={null}
        placeholder={t('addSkillPlaceholder')}
        allSkills={notSelectedSkills}
        onChange={handleAddSkill}
        className="small"
      />
    ) : (
      <Skeleton width={100} height={40} />
    );
  };

  const renderEmployeeSelect = () => {
    return (
      <SelectSingleEmployeeConnected
        value={null}
        heightMode="normal"
        employeeOptionLabelClassName="small"
        className="small"
        placeholder={t('addEmployeePlaceholder')}
        excludeIds={selectedEmployeeIds ?? []}
        onChange={handleAddEmployee}
      />
    );
  };

  if (
    !selectedEmployeeIds ||
    !selectedSkillIds ||
    !selectedSkills ||
    !selectedEmployeeInfos
  ) {
    return <Loading />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center px-4">
        <div>
          <Button
            label={t('resetMatrix')}
            onClick={handleResetClick}
            size="sm"
            variant="outline-secondary"></Button>
          <InfoHint text={t('resetMatrixHint')} className="ms-2" />
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-dark text-nowrap">
              {tGrouping('label')}
            </span>
            <ToggleGroup
              variant="secondary"
              value={grouping}
              onChange={(value) =>
                setGrouping(isSkillGroupBy(value) ? value : 'skill')
              }
              buttons={[
                { name: 'skill', title: tGrouping('bySkill') },
                { name: 'category', title: tGrouping('byCategory') },
                { name: 'branch', title: tGrouping('byBranch') },
              ]}
            />
          </div>
          <EmployeeFilterContainer>
            <input
              type="text"
              className="form-control form-control-sm"
              value={employeeFilter ?? ''}
              placeholder={t('employeeFilterPlaceholder')}
              onChange={(e) => setEmployeeFilter(e.target.value || null)}
            />
          </EmployeeFilterContainer>
          <SkillCategoryFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            className="small"
          />
          <SkillSelectContainer>{renderSkillSelect()}</SkillSelectContainer>
        </div>
      </div>
      {categoryFilter.length > 0 &&
        selectedSkills.length > 0 &&
        (filteredSelectedSkills?.length ?? 0) === 0 && (
          <div className="px-4 pt-3">
            <CategoryFilterEmptyState
              message={t('filterHidesAllSelectedSkills')}
              onClear={() => setCategoryFilter([])}
            />
          </div>
        )}
      <SkillsMatrixContainer className="py-4">
        <SkillsMatrix
          {...props}
          employees={filteredEmployees ?? []}
          skills={filteredSelectedSkills ?? []}
          grouping={grouping}
          resolveSkillName={resolveSkillName}
          emptyEmployeesText={emptyEmployeesText}
          employeeSkills={employeeSkills ?? 'loading'}
          onRemoveEmployeeClick={handleRemoveEmployee}
          onRemoveSkillClick={handleRemoveSkill}
          topLeftCell={renderEmployeeSelect()}
        />
      </SkillsMatrixContainer>
    </div>
  );
};

const Loading = () => {
  return (
    <div className="px-4 pb-4">
      <div className="d-flex justify-content-between align-items-center">
        <Skeleton width="5rem" height="1.5rem" />
        <Skeleton width="7.5rem" height="1.5rem" />
      </div>
      <Skeleton className="mt-4" width="100%" height={400} />
    </div>
  );
};

const SkillsMatrixContainer = styled.div({
  maxWidth: '100%',
  // Mirror the sticky first column's 1.5rem left inset so the last column
  // isn't flush against the card edge now that the grid fills full width.
  paddingRight: '1.5rem',
  overflowX: 'auto',
  overflowY: 'auto',
});

const SkillSelectContainer = styled.div({
  minWidth: '12rem',
});

const EmployeeFilterContainer = styled.div({
  minWidth: '11rem',
});
